import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy, Component, computed, EventEmitter, Inject,
  inject,
  input,
  OnDestroy, OnInit,
  Output,
  PLATFORM_ID,
  signal
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  FilterOptionsStore,
  InPersonRadius,
  IFilterForm,
  IFilterFormOptionValue,
  IFilterFormValues,
  ILatLon,
  PSYCHIATRIST_URL_TITLE,
} from '@sondermind/data-access/public-facing/filter-form';
import { FEATURE_FLAG_SERVICE, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { GoogleMapsGeocodeService } from '@sondermind/google-maps';
import { EmptyURIValue } from '@sondermind/data-access/directory-link';
import { IInsuranceOptionValue } from '@sondermind/utilities/models-provider-network-company';
import { BreakpointsService } from '@sondermind/utilities/services';
import { ProviderNameSearchComponent, SearchLayout } from '@sondermind/features/provider-name-search';
import { IFilterCategoryConfig } from '../../models/filter-category-config.interface';
import { FilterCategory } from '../../models/filter-name.constants';
import { IOptionCategory } from '../../models/option-category.interface';
import { FilterLoadingService } from '../../services/filter-loading.service';
import { FilterParseService } from '../../services/filter-parse.service';
import { FilterValidationService } from '../../services/filter-validation.service';
import { isInsuranceInOptions } from '../../utils/insurance-selection.util';
import { FilterSelectMobileComponent } from '../filter-select-mobile/filter-select-mobile.component';
import { FilterSelectV2Component } from '../filter-select-v2/filter-select-v2.component';
import { MoreFiltersV2Component } from '../more-filters-v2/more-filters-v2.component';
import { InPersonSearchStore } from '../../stores/in-person-search.store';

// How many compact chips are shown at each viewport width. The companion sidebar is a fixed 496px,
// so viewport width maps deterministically to the space left for chips; wider viewports pull more
// filters out of "More filters". Ordered ascending by minWidth.
const COMPACT_CHIP_TIERS: ReadonlyArray<{ minWidth: number; count: number; }> = [
  { minWidth: 1280, count: 2 },
  { minWidth: 1440, count: 3 },
  { minWidth: 1600, count: 4 },
  { minWidth: 1840, count: 5 }
];

// Fewest inline chips (Insurance only), used below the smallest tier and before first measure.
const BASE_COMPACT_CHIP_COUNT = 1;

// Stable data-test hooks the desktop chips already expose (used by e2e page objects).
const CHIP_DATA_TEST: Partial<Record<FilterCategory, string>> = {
  [FilterCategory.STATE]: 'location',
  [FilterCategory.THERAPY_OR_PSYCHIATRY]: 'therapy-or-psychiatry',
  [FilterCategory.INSURANCE]: 'insurance',
  [FilterCategory.SPECIALTY]: 'concerns'
};

// `size` attribute (character width) for a chip's text input. Matches the widths the full-desktop
// chips use so the compact row lines up; Insurance holds longer plan names so it is wider.
const enum ChipSize {
  DEFAULT = 8,
  INSURANCE = 12,
}

// A compact filter with presentation fields (data-test hook, input size) precomputed at build time
// so the template reads plain properties instead of calling functions on every change detection.
interface ICompactFilterChip extends IFilterCategoryConfig {
  dataTest: string;
  size: ChipSize;
}

@Component({
  selector: 'filter-form-v2',
  standalone: true,
  imports: [
    CommonModule,
    FilterSelectV2Component,
    MoreFiltersV2Component,
    FilterSelectMobileComponent,
    ReactiveFormsModule,
    ProviderNameSearchComponent,
  ],
  providers: [InPersonSearchStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './filter-form-v2.component.html',
  styleUrl: './filter-form-v2.component.scss'
})
export class FilterFormV2Component implements OnInit, OnDestroy {
  readonly internalDirectory = input<boolean>(false);

  @Output() filterRetrievalError = new EventEmitter<boolean>();
  @Output() redirectToNotFound = new EventEmitter<boolean>();
  @Output() formFiltersChanged = new EventEmitter<Partial<IFilterFormValues>>();
  stateOptions: Array<IOptionCategory<string>> = [];
  insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>> = [];
  specialtyOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  modalityOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  genderOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  sessionFormatOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  ageOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  treatmentPreferenceOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  serviceTypeOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];

  filterFormGroup: FormGroup<IFilterForm> = new FormGroup({
    city: new FormControl<string | null>(null, Validators.required),
    state: new FormControl<string | null>(null, Validators.required),
    serviceType: new FormControl<IFilterFormOptionValue | null>(null, Validators.required),
    insurance: new FormControl<IInsuranceOptionValue | null>(null),
    specialties: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    modalities: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    genders: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    sessionFormats: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    ages: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    treatmentPreferences: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    userLatLon: new FormControl<ILatLon | null>(null),
    userZip: new FormControl<string | null>(null),
    userRadius: new FormControl<InPersonRadius>(25, { nonNullable: true }),
  });
  moreFiltersListDesktop: IFilterCategoryConfig[] = [];
  concernsListMobile: IFilterCategoryConfig[] = [];
  moreFiltersListMobile: IFilterCategoryConfig[] = [];

  // Single ordered list for the compact (companion open) layout. The first N entries render as
  // inline chips (N chosen by viewport tier); the remainder overflow into `more-filters-v2`.
  readonly compactDesktopFilters = signal<ICompactFilterChip[]>([]);

  readonly isCompactDesktop = input<boolean>(false);
  readonly filtersLoading = signal<boolean>(true);
  readonly isMobile = signal<boolean>(false);
  readonly isSearchPanelOpen = signal<boolean>(false);

  // How many of `compactDesktopFilters` show inline; the rest go to More filters. Always >= 1 so
  // Insurance (index 0) stays visible. Driven by media queries (see subscribeToBreakpointChanges).
  private readonly visibleCompactCount = signal<number>(BASE_COMPACT_CHIP_COUNT);

  readonly visibleCompactFilters = computed(() => this.compactDesktopFilters().slice(0, this.visibleCompactCount()));

  readonly overflowCompactFilters = computed(() => this.compactDesktopFilters().slice(this.visibleCompactCount()));

  protected readonly filterCategory = FilterCategory;
  protected readonly searchLayout = SearchLayout;
  isUpdatingForm: boolean = false;
  private destroyed$ = new Subject<void>();

  readonly inPersonSearchStore = inject(InPersonSearchStore);
  private readonly breakpointObserver = inject(BreakpointObserver);

  constructor(
    private filterLoadingService: FilterLoadingService,
    private filterValidationService: FilterValidationService,
    private filtersStore: FilterOptionsStore,
    private filterParseService: FilterParseService,
    private breakpointService: BreakpointsService,
    private geocodeService: GoogleMapsGeocodeService,
    @Inject(PLATFORM_ID) private platformId: object,
    private route: ActivatedRoute,
    @Inject(FEATURE_FLAG_SERVICE) private featureFlagService: IFeatureFlagService,
  ) { }

  ngOnInit(): void {
    this.filtersLoading.set(true);

    // Initialize the in-person search store with form controls
    this.inPersonSearchStore.initializeControls({
      sessionFormats: this.filterFormGroup.controls.sessionFormats,
      userZip: this.filterFormGroup.controls.userZip,
      userLatLon: this.filterFormGroup.controls.userLatLon,
      userRadius: this.filterFormGroup.controls.userRadius,
    });

    this.subscribeToFormChanges();
    this.subscribeToFilterLoadingError();
    this.subscribeToFilterValidation();
    this.subscribeToBreakpointChanges();
    this.subscribeToZipChanges();
    this.loadAllFilters();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private loadAllFilters(): void {
    this.filterLoadingService.getAllFilterOptions$().pipe(
      take(1),
      filter((filterOptions) => !!filterOptions),
      tap((filterOptions) => {
        this.stateOptions = filterOptions.stateOptions;
        this.insuranceOptions = filterOptions.insuranceOptions;
        this.specialtyOptions = filterOptions.specialtyOptions;
        this.modalityOptions = filterOptions.modalityOptions;
        this.genderOptions = filterOptions.genderOptions;
        this.sessionFormatOptions = filterOptions.sessionFormatOptions;
        this.ageOptions = filterOptions.ageOptions;
        this.treatmentPreferenceOptions = filterOptions.treatmentPreferenceOptions;
        this.serviceTypeOptions = filterOptions.serviceTypeOptions;
      }),
      switchMap(() => this.populateFormFromUrl$())
    ).subscribe(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.filtersLoading.set(false);
      }
      this.setupFilterConfigurations();
      this.filtersStore.setIsLoaded(true);
      this.filterFormGroup.updateValueAndValidity();
    });
  }

  private populateFormFromUrl$(): Observable<string | null> {
    // Prevent subscribeToFormChanges from resetting city during initial URL parsing
    this.isUpdatingForm = true;

    return this.filterParseService.parseAndPopulateV2Form(
      this.route,
      this.filterFormGroup,
      {
        stateOptions: this.stateOptions,
        insuranceOptions: this.insuranceOptions,
        specialtyOptions: this.specialtyOptions,
        modalityOptions: this.modalityOptions,
        genderOptions: this.genderOptions,
        sessionFormatOptions: this.sessionFormatOptions,
        ageOptions: this.ageOptions,
        treatmentPreferenceOptions: this.treatmentPreferenceOptions,
        serviceTypeOptions: this.serviceTypeOptions
      }
    ).pipe(
      tap(() => {
        this.isUpdatingForm = false;
      }),
      map(() => this.filterFormGroup.controls.state.value)
    );
  }

  private setupFilterConfigurations(): void {
    this.setupDesktopFilters();
    this.setupCompactDesktopFilters();
    this.setupMobileFilters();
  }

  private setupDesktopFilters(): void {
    this.moreFiltersListDesktop = [
      {
        category: FilterCategory.GENDER_PREFERENCE,
        options: this.genderOptions,
        formControl: this.filterFormGroup.controls.genders,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.TREATMENT_APPROACH,
        options: this.treatmentPreferenceOptions,
        formControl: this.filterFormGroup.controls.treatmentPreferences,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.TYPE_OF_SERVICE,
        options: this.modalityOptions,
        formControl: this.filterFormGroup.controls.modalities,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.AGE_GROUP_TREATED,
        options: this.ageOptions,
        formControl: this.filterFormGroup.controls.ages,
        isSearchable: false,
        isMulti: true
      }
    ];
  }

  private setupCompactDesktopFilters(): void {
    // Ordered by display priority: Insurance is always visible (index 0); the rest fill the row
    // left-to-right until it runs out of width, then overflow into "More filters".
    const orderedFilters: IFilterCategoryConfig[] = [
      {
        category: FilterCategory.INSURANCE,
        options: this.insuranceOptions,
        formControl: this.filterFormGroup.controls.insurance,
        isSearchable: true,
        isMulti: false
      },
      {
        category: FilterCategory.SPECIALTY,
        options: this.specialtyOptions,
        formControl: this.filterFormGroup.controls.specialties,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.STATE,
        options: this.stateOptions,
        formControl: this.filterFormGroup.controls.state,
        isSearchable: true,
        isMulti: false
      },
      {
        category: FilterCategory.THERAPY_OR_PSYCHIATRY,
        options: this.serviceTypeOptions,
        formControl: this.filterFormGroup.controls.serviceType,
        isSearchable: false,
        isMulti: false
      },
      {
        category: FilterCategory.GENDER_PREFERENCE,
        options: this.genderOptions,
        formControl: this.filterFormGroup.controls.genders,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.TREATMENT_APPROACH,
        options: this.treatmentPreferenceOptions,
        formControl: this.filterFormGroup.controls.treatmentPreferences,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.TYPE_OF_SERVICE,
        options: this.modalityOptions,
        formControl: this.filterFormGroup.controls.modalities,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.SESSION_FORMAT,
        options: this.sessionFormatOptions,
        formControl: this.filterFormGroup.controls.sessionFormats,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.AGE_GROUP_TREATED,
        options: this.ageOptions,
        formControl: this.filterFormGroup.controls.ages,
        isSearchable: false,
        isMulti: true
      }
    ];

    this.compactDesktopFilters.set(orderedFilters.map((config) => this.toCompactChip(config)));
  }

  // Attach presentation fields once so the template never calls functions per chip per CD cycle.
  private toCompactChip(config: IFilterCategoryConfig): ICompactFilterChip {
    const dataTest = CHIP_DATA_TEST[config.category]
      ?? `filter-${config.category.toLowerCase().replace(/\s+/g, '-')}`;
    const size = config.category === FilterCategory.INSURANCE ? ChipSize.INSURANCE : ChipSize.DEFAULT;
    return { ...config, dataTest, size };
  }

  private setupMobileFilters(): void {
    this.concernsListMobile = [
      {
        category: FilterCategory.SPECIALTY,
        options: this.specialtyOptions,
        formControl: this.filterFormGroup.controls.specialties,
        isSearchable: true,
        isMulti: true
      }
    ];

    this.moreFiltersListMobile = [
      {
        category: FilterCategory.THERAPY_OR_PSYCHIATRY,
        options: this.serviceTypeOptions,
        formControl: this.filterFormGroup.controls.serviceType,
        isSearchable: false,
        isMulti: false
      },
      {
        category: FilterCategory.INSURANCE,
        options: this.insuranceOptions,
        formControl: this.filterFormGroup.controls.insurance,
        isSearchable: true,
        isMulti: false
      },
      {
        category: FilterCategory.GENDER_PREFERENCE,
        options: this.genderOptions,
        formControl: this.filterFormGroup.controls.genders,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.TREATMENT_APPROACH,
        options: this.treatmentPreferenceOptions,
        formControl: this.filterFormGroup.controls.treatmentPreferences,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.TYPE_OF_SERVICE,
        options: this.modalityOptions,
        formControl: this.filterFormGroup.controls.modalities,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.SESSION_FORMAT,
        options: this.sessionFormatOptions,
        formControl: this.filterFormGroup.controls.sessionFormats,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.AGE_GROUP_TREATED,
        options: this.ageOptions,
        formControl: this.filterFormGroup.controls.ages,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.STATE,
        options: this.stateOptions,
        formControl: this.filterFormGroup.controls.state,
        isSearchable: true,
        isMulti: false
      }
    ];
  }

  updateFormValues(formValues: Partial<IFilterFormValues>): void {
    this.filterFormGroup.patchValue(formValues);
  }

  private updateMobileInsuranceFilter(): void {
    const insuranceFilterIndex = this.moreFiltersListMobile.findIndex(
      (filterConfig) => filterConfig.category === FilterCategory.INSURANCE
    );
    if (insuranceFilterIndex !== -1) {
      this.moreFiltersListMobile[insuranceFilterIndex] = {
        ...this.moreFiltersListMobile[insuranceFilterIndex],
        options: this.insuranceOptions
      };
    }

    this.updateCompactInsuranceFilter();
  }

  private updateCompactInsuranceFilter(): void {
    this.compactDesktopFilters.update((filters) => filters.map((filterConfig) => {
      if (filterConfig.category !== FilterCategory.INSURANCE) return filterConfig;
      return { ...filterConfig, options: this.insuranceOptions };
    }));
  }

  private updateInsuranceOptions$() {
    return this.filterLoadingService.getInsuranceOptions$().pipe(
      map((insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>>) => {
        this.insuranceOptions = insuranceOptions;
        this.updateMobileInsuranceFilter();
        return this.filterFormGroup.value;
      })
    );
  }

  private subscribeToFilterLoadingError(): void {
    // Handle errors from loader
    this.filterLoadingService.filterError$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((hasError) => {
      if (hasError) {
        this.filterRetrievalError.emit(true);
      }
    });
  }

  private subscribeToFilterValidation(): void {
    this.filterValidationService.setupFilterValidation$(this.route).pipe(
      takeUntil(this.destroyed$)
    ).subscribe();

    this.filterValidationService.invalidFilterState$.pipe(
      takeUntil(this.destroyed$),
      filter((x) => x),
      // Validators run synchronously in one tick, so several can flip the state to true
      // in the same navigation. take(1) collapses those into a single redirect.
      take(1)
    ).subscribe(() => {
      this.redirectToNotFound.emit(true);
    });
  }

  private subscribeToBreakpointChanges(): void {
    this.breakpointService.isMobile$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isMobile) => {
      this.isMobile.set(isMobile);
    });

    this.subscribeToCompactChipTierChanges();
  }

  private subscribeToCompactChipTierChanges(): void {
    const tierQueries = COMPACT_CHIP_TIERS.map((tier) => `(min-width: ${tier.minWidth}px)`);

    this.breakpointObserver.observe(tierQueries).pipe(
      map((state) => this.widestMatchedTierCount(state.breakpoints)),
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    ).subscribe((count) => {
      this.visibleCompactCount.set(count);
    });
  }

  // Highest tier whose min-width query currently matches; base count (Insurance only) if none do.
  private widestMatchedTierCount(matches: Record<string, boolean>): number {
    let count = BASE_COMPACT_CHIP_COUNT;
    COMPACT_CHIP_TIERS.forEach((tier) => {
      if (matches[`(min-width: ${tier.minWidth}px)`]) {
        count = tier.count;
      }
    });
    return count;
  }

  updateFormWithPsychiatricOption(): void {
    // Keep current state and city values
    const currentState = this.filterFormGroup.controls.state.value;
    const currentCity = this.filterFormGroup.controls.city.value;

    // Find the psychiatry option from the modality options
    const psychiatryOption = this.modalityOptions[0]?.options?.find(
      (option) => option.value.title === PSYCHIATRIST_URL_TITLE
    )?.value;

    if (!psychiatryOption) {
      return; // Psychiatry option not found
    }

    // Create form values with psychiatry option
    const psychiatricFormValues: Partial<IFilterFormValues> = {
      state: currentState,
      city: currentCity,
      insurance: null,
      specialties: [],
      modalities: [psychiatryOption],
      genders: [],
      sessionFormats: [],
      ages: [],
      treatmentPreferences: []
    };

    this.updateFormValues(psychiatricFormValues);
  }

  private subscribeToZipChanges(): void {
    this.filterFormGroup.controls.userZip.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        this.inPersonSearchStore.zipError.set(null);
        this.inPersonSearchStore.zipLoading.set(false);
      }),
      switchMap((zip) => {
        if (!zip || !/^\d{5}$/.test(zip)) {
          this.filterFormGroup.controls.userLatLon.setValue(null, { emitEvent: false });
          return EMPTY;
        }
        this.inPersonSearchStore.zipLoading.set(true);
        return this.geocodeService.forwardGeocode(zip).pipe(
          catchError(() => {
            this.inPersonSearchStore.zipError.set('Could not reach location service. Please try again.');
            return of(null);
          })
        );
      }),
      takeUntil(this.destroyed$)
    ).subscribe((latLon: ILatLon | null) => {
      this.inPersonSearchStore.zipLoading.set(false);
      this.filterFormGroup.controls.userLatLon.setValue(latLon, { emitEvent: false });

      if (latLon === null) {
        this.inPersonSearchStore.zipError.set('Zip code not found. Try another.');
      } else {
        this.formFiltersChanged.emit(this.filterFormGroup.value);
      }
    });
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.valueChanges.pipe(
      filter(() => !this.isUpdatingForm),
      switchMap((values) => {
        if (!!values?.state && values.state !== this.filtersStore.getCurrentState()) {
          this.isUpdatingForm = true;
          this.filterFormGroup.patchValue({ city: EmptyURIValue, insurance: null });
          this.filtersStore.setCurrentState(values.state);
          this.isUpdatingForm = false;
          return this.updateInsuranceOptions$();
        }
        // Update insurance options when switching between therapy and psychiatry
        const isPsychiatrist = values?.serviceType?.title === PSYCHIATRIST_URL_TITLE;
        const didIsPsychiatristChange = isPsychiatrist !== this.filtersStore.getCurrentIsPsychiatrist();
        if (didIsPsychiatristChange) {
          this.isUpdatingForm = true;
          this.filtersStore.setCurrentIsPsychiatrist(isPsychiatrist);
          return this.updateInsuranceOptions$().pipe(
            map(() => {
              const selectedInsurance = this.filterFormGroup.value.insurance;
              if (!isInsuranceInOptions(selectedInsurance, this.insuranceOptions)) {
                this.filterFormGroup.patchValue({ insurance: null });
              }
              this.isUpdatingForm = false;
              return this.filterFormGroup.value;
            })
          );
        }

        return of(values);
      }),
      takeUntil(this.destroyed$)
    ).subscribe((values) => {
      this.formFiltersChanged.emit(values);
    });
  }
}
